/**
 * ImageUploader — shared local image picker for the K-series admin catalogue.
 *
 * Founder ruling 14 Jun 2026 PM (later still · post-M1 sign-off):
 *   "Local image upload is mandatory. URL-only is not acceptable.
 *   Show a soft recommendation in the UI for better quality:
 *   Recommended: 1200 × 800 or higher."
 *
 * Behaviour:
 *   · Local file picker (accept=image/jpeg,image/png,image/webp)
 *   · Client-side preview via URL.createObjectURL (NO half-upload state on backend)
 *   · Pre-flight dimension + size check (matches backend caps · clearer error)
 *   · Confirm-to-upload flow (operator clicks "Confirm upload")
 *   · Replaces existing image · backend archives the old public_id
 *   · "Remove image" action available when one is present
 *
 * Props:
 *   currentImageUrl   string | null
 *   currentDims       { width?, height? }
 *   onUpload          async (File) => Promise<row>
 *   onRemove          async () => Promise<row>
 *   minWidth          number (default 600)
 *   minHeight         number (default 400)
 *   maxMb             number (default 8)
 *   disabled          bool
 *
 * The component is dumb — it owns no business logic. It calls onUpload
 * with the selected File · the parent (EnjoySignatureCRUD) calls the
 * /image/upload endpoint via the api helper.
 */
import React, { useEffect, useRef, useState } from "react";
import { resizeImageForUpload } from "@/lib/imageResize";

const RECOMMENDED_MIN_WIDTH = 1200;
const RECOMMENDED_MIN_HEIGHT = 800;

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];

export default function ImageUploader({
    currentImageUrl,
    currentDims = {},
    onUpload,
    onRemove,
    minWidth = 600,
    minHeight = 400,
    maxMb = 40,
    disabled = false,
}) {
    const [previewUrl, setPreviewUrl] = useState(null);
    const [pickedFile, setPickedFile] = useState(null);
    const [pickedDims, setPickedDims] = useState({ width: 0, height: 0 });
    const [validationError, setValidationError] = useState(null);
    const [busy, setBusy] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl]);

    const handlePick = async (e) => {
        const rawPick = e.target.files?.[0];
        if (!rawPick) return;
        setValidationError(null);

        if (!ALLOWED_TYPES.includes(rawPick.type)) {
            setValidationError(
                `File type ${rawPick.type || "unknown"} is not supported · please choose JPEG / PNG / WEBP.`,
            );
            return;
        }

        // v3.48 · Client-side resize before validation. Turns a 25 MB
        // ProRAW into a ~1–2 MB JPEG that comfortably clears the ingress
        // limit and uploads fast on cellular. Non-resamplable files
        // (HEIC, tiny PNG icons) pass through unchanged.
        const file = await resizeImageForUpload(rawPick, { maxDim: 2500, quality: 0.88 });

        const sizeMb = file.size / (1024 * 1024);
        if (sizeMb > maxMb) {
            setValidationError(
                `Image is ${sizeMb.toFixed(1)} MB · the limit is ${maxMb} MB.`,
            );
            return;
        }

        // Read dimensions client-side
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            const { width, height } = img;
            URL.revokeObjectURL(url);
            const tooSmall = width < minWidth || height < minHeight;
            if (tooSmall) {
                setValidationError(
                    `Image ${width}×${height} is below the minimum ${minWidth}×${minHeight}. ` +
                    `Please pick a larger image.`,
                );
                setPickedFile(null);
                setPreviewUrl(null);
                return;
            }
            const previewObjectUrl = URL.createObjectURL(file);
            setPreviewUrl(previewObjectUrl);
            setPickedFile(file);
            setPickedDims({ width, height });
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            setValidationError("Could not decode the image · please pick another file.");
        };
        img.src = url;
    };

    const handleCancelPick = () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
        setPickedFile(null);
        setPickedDims({ width: 0, height: 0 });
        setValidationError(null);
        if (inputRef.current) inputRef.current.value = "";
    };

    const handleConfirmUpload = async () => {
        if (!pickedFile || !onUpload) return;
        setBusy(true);
        try {
            await onUpload(pickedFile);
            handleCancelPick();
        } catch (e) {
            setValidationError(
                e?.response?.data?.detail?.message ||
                e?.message ||
                "Upload failed. Please try again.",
            );
        } finally {
            setBusy(false);
        }
    };

    const handleRemoveCurrent = async () => {
        if (!onRemove) return;
        if (!window.confirm("Remove the current image? The row will return to draft if it was live.")) return;
        setBusy(true);
        try {
            await onRemove();
        } catch (e) {
            setValidationError(
                e?.response?.data?.detail?.message ||
                e?.message ||
                "Remove failed.",
            );
        } finally {
            setBusy(false);
        }
    };

    const showsSoftRecommendation =
        pickedDims.width > 0 && (
            pickedDims.width < RECOMMENDED_MIN_WIDTH ||
            pickedDims.height < RECOMMENDED_MIN_HEIGHT
        );

    return (
        <div className="k1-uploader" data-testid="k1-image-uploader">
            <style>{`
                .k1-uploader {
                    display: flex; flex-direction: column; gap: 10px;
                    padding: 12px;
                    border: 1px dashed #999;
                    border-radius: 8px;
                    background: #fafafa;
                }
                .k1-uploader__preview {
                    max-width: 100%;
                    max-height: 220px;
                    object-fit: contain;
                    border-radius: 4px;
                    background: #fff;
                }
                .k1-uploader__row {
                    display: flex; gap: 8px; align-items: center; flex-wrap: wrap;
                }
                .k1-uploader__hint { color: #666; font-size: 12px; }
                .k1-uploader__softrec {
                    color: #b86c00; font-size: 12px;
                    background: #fff7e6; padding: 6px 8px; border-radius: 4px;
                }
                .k1-uploader__err {
                    color: #a02020; font-size: 13px;
                    background: #fdecea; padding: 6px 8px; border-radius: 4px;
                }
                .k1-uploader button {
                    padding: 6px 12px; border-radius: 4px;
                    border: 1px solid #ccc; background: #fff; cursor: pointer;
                }
                .k1-uploader button:disabled {
                    opacity: 0.5; cursor: not-allowed;
                }
                .k1-uploader__primary {
                    background: #1f2d3d !important; color: white !important;
                    border-color: #1f2d3d !important;
                }
                .k1-uploader__danger {
                    background: #fff !important; color: #a02020 !important;
                    border-color: #c08080 !important;
                }
            `}</style>

            {/* Current image preview */}
            {currentImageUrl ? (
                <div className="k1-uploader__row" data-testid="k1-image-current">
                    <img
                        src={currentImageUrl}
                        alt="Current"
                        className="k1-uploader__preview"
                        data-testid="k1-image-current-preview"
                    />
                    <div className="k1-uploader__hint">
                        {currentDims.width ? `${currentDims.width}×${currentDims.height}` : ""}
                    </div>
                    <button
                        type="button"
                        className="k1-uploader__danger"
                        onClick={handleRemoveCurrent}
                        disabled={disabled || busy}
                        data-testid="k1-image-remove-btn"
                    >
                        Remove image
                    </button>
                </div>
            ) : (
                <div className="k1-uploader__hint" data-testid="k1-image-empty">
                    No image yet · publishing is blocked until one is uploaded.
                </div>
            )}

            {/* Picker for a new / replacement image */}
            <div className="k1-uploader__row">
                <input
                    ref={inputRef}
                    type="file"
                    accept={ALLOWED_TYPES.join(",")}
                    onChange={handlePick}
                    disabled={disabled || busy}
                    data-testid="k1-image-file-input"
                />
                <div className="k1-uploader__hint">
                    Hard min: {minWidth}×{minHeight} · max {maxMb} MB · Recommended {RECOMMENDED_MIN_WIDTH}×{RECOMMENDED_MIN_HEIGHT}+ for premium quality
                </div>
            </div>

            {validationError ? (
                <div className="k1-uploader__err" data-testid="k1-image-validation-error">
                    {validationError}
                </div>
            ) : null}

            {/* Preview of the picked-but-not-yet-uploaded file */}
            {previewUrl ? (
                <div className="k1-uploader__row" data-testid="k1-image-picked">
                    <img
                        src={previewUrl}
                        alt="Preview"
                        className="k1-uploader__preview"
                        data-testid="k1-image-picked-preview"
                    />
                    <div className="k1-uploader__hint">
                        Picked: {pickedDims.width}×{pickedDims.height} · {(pickedFile?.size / 1024).toFixed(0)} KB
                    </div>
                    {showsSoftRecommendation ? (
                        <div className="k1-uploader__softrec" data-testid="k1-image-soft-rec">
                            Recommended: {RECOMMENDED_MIN_WIDTH}×{RECOMMENDED_MIN_HEIGHT} or higher for premium quality.
                            The image is acceptable but will look better on retina screens at the recommended size.
                        </div>
                    ) : null}
                    <button
                        type="button"
                        className="k1-uploader__primary"
                        onClick={handleConfirmUpload}
                        disabled={disabled || busy || !!validationError}
                        data-testid="k1-image-confirm-upload-btn"
                    >
                        {busy ? "Uploading…" : "Confirm upload"}
                    </button>
                    <button
                        type="button"
                        onClick={handleCancelPick}
                        disabled={busy}
                        data-testid="k1-image-cancel-pick-btn"
                    >
                        Cancel
                    </button>
                </div>
            ) : null}
        </div>
    );
}
